/**
 * Wallet Details Page
 * Modern DeBank-style wallet view with holdings sorted by value
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { useWalletTrackerStore } from '../store/walletTrackerStore';
import { useAlertStore, type AlertCondition, type AlertChannel , Alert } from '../store/alertStore';
import { useAggregatedWalletData, formatUSD, formatTokenBalance, type UnifiedToken, type DeFiPosition } from '../hooks/wallet-tracker/useAggregatedWalletData';
import { getProviderStatus, isDebankCreditsExhausted, clearWalletCache } from '../services/walletDataAggregator';
import { TRACKER_COLORS } from '../config/monitorColors';
import type { TrackedWallet } from '../types/wallet.types';

// ============================================
// CONSTANTS & STYLES
// ============================================

type DetailsTab = 'holdings' | 'defi';

const TAG_CONFIG: Record<string, { emoji: string; color: string }> = {
  'smart-money': { emoji: '🧠', color: '#8b5cf6' },
  'whale': { emoji: '🐋', color: '#3b82f6' },
  'defi-pro': { emoji: '🏦', color: '#10b981' },
  'nft-trader': { emoji: '🎨', color: '#ec4899' },
  'influencer': { emoji: '⭐', color: '#f59e0b' },
  'fund': { emoji: '💼', color: '#6366f1' },
  'personal': { emoji: '👤', color: '#64748b' },
};

const ALERT_CONDITIONS: Record<AlertCondition, { label: string; icon: string; color: string; description: string }> = {
  'token_buy': { label: 'Buy', icon: '📥', color: '#10b981', description: 'When this wallet buys the token' },
  'token_sell': { label: 'Sell', icon: '📤', color: '#ef4444', description: 'When this wallet sells the token' },
  'token_transfer': { label: 'Transfer', icon: '↔️', color: '#f59e0b', description: 'Any transfer in or out' },
  'transfer_in': { label: 'Receive', icon: '⬇️', color: '#10b981', description: 'When tokens are received' },
  'transfer_out': { label: 'Send', icon: '⬆️', color: '#ef4444', description: 'When tokens are sent out' },
  'swap': { label: 'Swap', icon: '🔄', color: '#8b5cf6', description: 'When tokens are swapped' },
  'any_transaction': { label: 'Any', icon: '📊', color: '#06b6d4', description: 'Any transaction' },
  'value_threshold': { label: 'Value', icon: '💰', color: '#f59e0b', description: 'Above value threshold' },
};

const s = {
  page: {
    padding: '0',
    maxWidth: '1200px',
    margin: '0 auto',
    paddingBottom: '40px',
  },
  backButton: {
    padding: '10px 16px',
    fontSize: '13px',
    fontWeight: 500,
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    color: 'var(--text)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '20px',
    transition: 'all 0.2s',
  },
  headerCard: {
    background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.12), rgba(8, 145, 178, 0.08))',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    border: '1px solid rgba(6, 182, 212, 0.2)',
    padding: '28px',
    marginBottom: '24px',
  },
  addressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap' as const,
  },
  addressText: {
    fontSize: '14px',
    fontFamily: 'monospace',
    color: 'var(--muted)',
    background: 'rgba(0, 0, 0, 0.3)',
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  iconButton: {
    padding: '10px',
    background: 'rgba(255, 255, 255, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    color: 'var(--text)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    gap: '6px',
    fontSize: '11px',
    fontWeight: 500,
  },
  totalValue: {
    fontSize: '42px',
    fontWeight: 700,
    color: 'var(--text)',
    marginBottom: '8px',
  },
  card: {
    background: 'rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
    background: 'rgba(0, 0, 0, 0.2)',
  },
  tokenRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px 24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
    transition: 'all 0.2s ease',
    cursor: 'default',
  },
  tokenIcon: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    background: 'rgba(6, 182, 212, 0.15)',
    border: '2px solid rgba(6, 182, 212, 0.2)',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '60px 20px',
  },
  tabContainer: {
    display: 'flex',
    gap: '4px',
    padding: '4px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '14px',
    width: 'fit-content',
    marginBottom: '20px',
  },
  tab: (isActive: boolean, color: string) => ({
    padding: '12px 24px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    transition: 'all 0.3s ease',
    background: isActive ? `linear-gradient(135deg, ${color}, ${color}80)` : 'transparent',
    color: isActive ? 'white' : 'var(--muted)',
    transform: isActive ? 'scale(1.02)' : 'scale(1)',
    boxShadow: isActive ? `0 4px 15px ${color}40` : 'none',
  }),
  tabBadge: (isActive: boolean, color: string) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '24px',
    height: '24px',
    padding: '0 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 700,
    background: isActive ? 'rgba(255,255,255,0.25)' : `${color}20`,
    color: isActive ? 'white' : color,
  }),
};

// ============================================
// TYPES
// ============================================

interface WalletDetailsProps {
  address: string;
  onBack: () => void;
}

// ============================================
// COMPONENT
// ============================================

export function WalletDetails({ address, onBack }: WalletDetailsProps) {
  const { t } = useTranslation();
  const { wallets } = useWalletTrackerStore();
  const { alerts, addAlert, removeAlert, toggleAlert } = useAlertStore();
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<DetailsTab>('holdings');

  // Alert creation state
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [selectedTokenForAlert, setSelectedTokenForAlert] = useState<{ address: string; symbol: string; name: string } | null>(null);
  const [alertCondition, setAlertCondition] = useState<AlertCondition>('token_transfer');
  const [alertChannels, setAlertChannels] = useState<AlertChannel[]>(['in_app']);

  // Find wallet
  const wallet = wallets.find((w: TrackedWallet) => w.address.toLowerCase() === address.toLowerCase());

  // Get provider status
  const providerStatus = getProviderStatus();
  const activeProvider = providerStatus.debank.available
    ? 'DeBank'
    : providerStatus.moralis.available
    ? 'Moralis'
    : providerStatus.alchemy.available
    ? 'Alchemy'
    : 'None';

  // Get alerts for this wallet
  const walletAlerts = useMemo(() =>
    alerts.filter((a: Alert) => a.walletAddress.toLowerCase() === address.toLowerCase()),
    [alerts, address]
  );

  // Fetch aggregated wallet data (DeBank primary, Moralis fallback)
  const { data: walletData, isLoading } = useAggregatedWalletData(address, {
    enabled: !!wallet,
    includeNFTs: false, // Don't fetch NFTs for performance
    includeDeFi: true,
  });

  // Sorted tokens from aggregated data (filter out tokens worth less than $1)
  const sortedTokens = useMemo(() => {
    if (!walletData?.tokens) return [];
    // Filter tokens with value >= $1 and already sorted by balanceUSD from the aggregator
    return walletData.tokens.filter(token => token.balanceUSD >= 1);
  }, [walletData]);

  // Count of hidden small tokens for display
  const hiddenSmallTokensCount = useMemo(() => {
    if (!walletData?.tokens) return 0;
    return walletData.tokens.filter(token => token.balanceUSD < 1).length;
  }, [walletData]);

  // DeFi positions
  const defiPositions = useMemo(() => {
    if (!walletData?.defiPositions) return [];
    return walletData.defiPositions;
  }, [walletData]);

  // Total portfolio value
  const totalValueUSD = walletData?.totalValueUSD || 0;

  // Copy address handler
  const handleCopyAddress = () => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Open alert modal for a specific token
  const openAlertModal = (token: { address: string; symbol: string; name: string }) => {
    setSelectedTokenForAlert(token);
    setAlertCondition('token_transfer');
    setAlertChannels(['in_app']);
    setShowAlertModal(true);
  };

  // Create a new alert
  const handleCreateAlert = () => {
    if (!selectedTokenForAlert || !wallet) return;

    addAlert({
      walletAddress: address,
      walletLabel: wallet.label,
      condition: alertCondition,
      enabled: true,
      channels: alertChannels,
      tokenAddress: selectedTokenForAlert.address,
      tokenSymbol: selectedTokenForAlert.symbol,
      tokenName: selectedTokenForAlert.name,
    });

    setShowAlertModal(false);
    setSelectedTokenForAlert(null);
  };

  // Toggle channel selection
  const toggleChannel = (channel: AlertChannel) => {
    setAlertChannels(prev =>
      prev.includes(channel)
        ? prev.filter(c => c !== channel)
        : [...prev, channel]
    );
  };

  // Get explorer URL based on chain
  const getExplorerUrl = (chain: string, tokenAddress: string, walletAddress: string): string => {
    const explorers: Record<string, string> = {
      ethereum: 'https://etherscan.io',
      bsc: 'https://bscscan.com',
      polygon: 'https://polygonscan.com',
      arbitrum: 'https://arbiscan.io',
      optimism: 'https://optimistic.etherscan.io',
      avalanche: 'https://snowtrace.io',
      base: 'https://basescan.org',
      fantom: 'https://ftmscan.com',
      gnosis: 'https://gnosisscan.io',
    };
    const baseUrl = explorers[chain] || explorers.ethereum;
    return `${baseUrl}/token/${tokenAddress}?a=${walletAddress}`;
  };

  // Wallet not found
  if (!wallet) {
    return (
      <div style={s.page}>
        <div style={{ ...s.card, ...s.emptyState }}>
          <Icons.AlertCircle size={48} style={{ opacity: 0.3, marginBottom: '16px', color: TRACKER_COLORS.error }} />
          <h2 style={{ fontSize: '20px', marginBottom: '8px' }}>
            {t('walletTracker.details.notFound', 'Wallet Not Found')}
          </h2>
          <p style={{ color: 'var(--muted)', marginBottom: '20px' }}>
            {t('walletTracker.details.notFoundDesc', 'The requested wallet could not be found.')}
          </p>
          <button onClick={onBack} style={{ ...s.backButton, margin: '0 auto' }}>
            <Icons.ArrowLeft size={16} />
            {t('walletTracker.details.backToWallets', 'Back to Wallets')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      {/* Back Button */}
      <button
        onClick={onBack}
        style={s.backButton}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
          e.currentTarget.style.borderColor = 'rgba(6, 182, 212, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
        }}
      >
        <Icons.ArrowLeft size={16} />
        {t('walletTracker.details.backToWallets', 'Back to Wallets')}
      </button>

      {/* Header Card */}
      <div style={s.headerCard}>
        {/* Wallet Label & Tags */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '14px',
            background: `linear-gradient(135deg, ${TRACKER_COLORS.primary}, ${TRACKER_COLORS.secondary})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Icons.Wallet size={24} color="white" />
          </div>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0, color: 'var(--text)' }}>
              {wallet.label}
            </h1>
            {wallet.ensName && (
              <span style={{ fontSize: '14px', color: TRACKER_COLORS.primary }}>{wallet.ensName}</span>
            )}
          </div>
          {/* Tags */}
          {wallet.tags && wallet.tags.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', marginLeft: '8px' }}>
              {wallet.tags.map((tag: string) => {
                const config = TAG_CONFIG[tag] || { emoji: '🏷️', color: '#64748b' };
                return (
                  <span
                    key={tag}
                    style={{
                      fontSize: '11px',
                      padding: '4px 10px',
                      borderRadius: '16px',
                      background: `${config.color}20`,
                      color: config.color,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontWeight: 600,
                    }}
                  >
                    <span>{config.emoji}</span>
                    <span style={{ textTransform: 'capitalize' }}>{tag.replace('-', ' ')}</span>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Address */}
        <div style={s.addressContainer}>
          <span style={s.addressText}>{address}</span>
          <button
            onClick={handleCopyAddress}
            style={{
              ...s.iconButton,
              background: copied ? `${TRACKER_COLORS.success}20` : 'rgba(255, 255, 255, 0.08)',
              borderColor: copied ? TRACKER_COLORS.success : 'rgba(255, 255, 255, 0.1)',
              color: copied ? TRACKER_COLORS.success : 'var(--text)',
            }}
            title={t('common.copy', 'Copy')}
          >
            {copied ? <Icons.Check size={16} /> : <Icons.Copy size={16} />}
          </button>
        </div>

        {/* External Links */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <a
            href={`https://etherscan.io/address/${address}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...s.iconButton, textDecoration: 'none', padding: '8px 14px' }}
            title="View on Etherscan"
          >
            <Icons.ExternalLink size={14} />
            <span>Etherscan</span>
          </a>
          <a
            href={`https://debank.com/profile/${address}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...s.iconButton, textDecoration: 'none', padding: '8px 14px' }}
            title="View on DeBank"
          >
            <Icons.Globe size={14} />
            <span>DeBank</span>
          </a>
          <a
            href={`https://platform.arkhamintelligence.com/explorer/address/${address}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...s.iconButton, textDecoration: 'none', padding: '8px 14px' }}
            title="View on Arkham"
          >
            <Icons.Eye size={14} />
            <span>Arkham</span>
          </a>
        </div>

        {/* Total Portfolio Value */}
        <div>
          <div style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {t('walletTracker.details.totalValue', 'Total Portfolio Value')}
            <span style={{
              fontSize: '10px',
              padding: '2px 8px',
              borderRadius: '10px',
              background: 'rgba(6, 182, 212, 0.15)',
              color: TRACKER_COLORS.primary,
              fontWeight: 500,
            }}>
              via {activeProvider}
            </span>
          </div>
          <div style={s.totalValue}>
            {isLoading ? (
              <span style={{ opacity: 0.5 }}>...</span>
            ) : (
              formatUSD(totalValueUSD)
            )}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
            {sortedTokens.length} {t('walletTracker.details.assets', 'assets')}
            {defiPositions.length > 0 && ` • ${defiPositions.length} DeFi positions`}
          </div>
        </div>

        {/* Notes */}
        {wallet.notes && (
          <div style={{
            marginTop: '20px',
            padding: '14px 16px',
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '12px',
            fontSize: '13px',
            color: 'var(--muted)',
            fontStyle: 'italic',
            borderLeft: `3px solid ${TRACKER_COLORS.primary}`,
          }}>
            "{wallet.notes}"
          </div>
        )}
      </div>

      {/* Holdings / DeFi Tabs */}
      <div style={s.tabContainer}>
        <button
          onClick={() => setActiveTab('holdings')}
          style={s.tab(activeTab === 'holdings', TRACKER_COLORS.primary)}
        >
          <Icons.Coins size={18} />
          {t('walletTracker.details.holdings', 'Holdings')}
          <span style={s.tabBadge(activeTab === 'holdings', TRACKER_COLORS.primary)}>
            {sortedTokens.length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('defi')}
          style={s.tab(activeTab === 'defi', TRACKER_COLORS.purple)}
        >
          <Icons.TrendingUp size={18} />
          {t('walletTracker.details.defiPositions', 'DeFi')}
          <span style={s.tabBadge(activeTab === 'defi', TRACKER_COLORS.purple)}>
            {defiPositions.length}
          </span>
        </button>
      </div>

      {/* Holdings Section */}
      {activeTab === 'holdings' && (
      <div style={s.card}>
        <div style={s.sectionHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icons.Coins size={20} style={{ color: TRACKER_COLORS.primary }} />
            <h2 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>
              {t('walletTracker.details.holdings', 'Holdings')}
            </h2>
          </div>
          <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
            {t('walletTracker.details.sortedByValue', 'Sorted by value')}
          </span>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div style={{ padding: '60px 20px', textAlign: 'center' }}>
            <Icons.Loader size={32} style={{ color: TRACKER_COLORS.primary, animation: 'spin 1s linear infinite', marginBottom: '16px' }} />
            <p style={{ fontSize: '14px', color: 'var(--muted)' }}>
              {t('walletTracker.details.loading', 'Loading holdings...')}
            </p>
          </div>
        )}

        {/* Holdings List */}
        {!isLoading && (
          <div>
            {/* Token Rows */}
            {sortedTokens.map((token: UnifiedToken, index: number) => {
              const hasAlert = walletAlerts.some((a: Alert) => a.tokenAddress?.toLowerCase() === token.address.toLowerCase());

              return (
                <div
                  key={`${token.chain}:${token.address}`}
                  style={{
                    ...s.tokenRow,
                    borderBottom: index === sortedTokens.length - 1 ? 'none' : '1px solid rgba(255, 255, 255, 0.04)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(6, 182, 212, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {/* Rank */}
                  <div style={{
                    width: '28px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'var(--muted)',
                    textAlign: 'center',
                  }}>
                    {index + 1}
                  </div>

                  {/* Icon */}
                  <div style={s.tokenIcon}>
                    {token.logoUrl ? (
                      <img
                        src={token.logoUrl}
                        alt={token.symbol}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <Icons.Coins size={22} style={{ color: TRACKER_COLORS.primary, opacity: 0.6 }} />
                    )}
                  </div>

                  {/* Name & Symbol */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '15px',
                      fontWeight: 600,
                      color: 'var(--text)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}>
                      {token.name}
                      {token.isVerified && (
                        <Icons.Check size={12} style={{ color: TRACKER_COLORS.success }} />
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {token.symbol}
                      {token.chain !== 'ethereum' && (
                        <span style={{
                          fontSize: '9px',
                          padding: '1px 5px',
                          borderRadius: '4px',
                          background: 'rgba(139, 92, 246, 0.2)',
                          color: TRACKER_COLORS.purple,
                          textTransform: 'uppercase',
                        }}>
                          {token.chain}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Price & Change */}
                  <div style={{ textAlign: 'right', minWidth: '100px' }}>
                    {token.price > 0 ? (
                      <>
                        <div style={{ fontSize: '13px', color: 'var(--text)' }}>
                          {token.price < 0.01 ? `$${token.price.toFixed(6)}` : formatUSD(token.price)}
                        </div>
                        {token.priceChange24h !== undefined && (
                          <div style={{
                            fontSize: '12px',
                            color: token.priceChange24h >= 0 ? TRACKER_COLORS.success : TRACKER_COLORS.error,
                          }}>
                            {token.priceChange24h >= 0 ? '+' : ''}{token.priceChange24h.toFixed(2)}%
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>-</div>
                    )}
                  </div>

                  {/* Balance & Value */}
                  <div style={{ textAlign: 'right', minWidth: '140px' }}>
                    <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>
                      {token.balanceUSD > 0 ? formatUSD(token.balanceUSD) : '-'}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                      {formatTokenBalance(token.balance)} {token.symbol}
                    </div>
                  </div>

                  {/* Track Alert Button */}
                  <button
                    onClick={() => openAlertModal({
                      address: token.address,
                      symbol: token.symbol,
                      name: token.name,
                    })}
                    style={{
                      padding: '6px 10px',
                      background: hasAlert ? `${TRACKER_COLORS.warning}20` : 'rgba(255, 255, 255, 0.05)',
                      border: hasAlert ? `1px solid ${TRACKER_COLORS.warning}50` : '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      color: hasAlert ? TRACKER_COLORS.warning : 'var(--muted)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '11px',
                      fontWeight: 500,
                      transition: 'all 0.2s',
                    }}
                    title={t('walletTracker.details.trackToken', 'Track this token')}
                  >
                    <Icons.Bell size={12} />
                    Track
                  </button>

                  {/* External Link */}
                  <a
                    href={getExplorerUrl(token.chain, token.address, address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: TRACKER_COLORS.primary, padding: '8px' }}
                  >
                    <Icons.ExternalLink size={16} />
                  </a>
                </div>
              );
            })}

            {/* Hidden small tokens note */}
            {hiddenSmallTokensCount > 0 && (
              <div style={{
                padding: '12px 24px',
                borderTop: '1px solid rgba(255, 255, 255, 0.04)',
                fontSize: '12px',
                color: 'var(--muted)',
                textAlign: 'center',
              }}>
                {t('walletTracker.details.hiddenTokens', '{{count}} token(s) with value < $1 hidden', { count: hiddenSmallTokensCount })}
              </div>
            )}

            {/* Empty State */}
            {sortedTokens.length === 0 && hiddenSmallTokensCount === 0 && (
              <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                <Icons.Coins size={48} style={{ opacity: 0.3, marginBottom: '16px', color: TRACKER_COLORS.primary }} />
                <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
                  {t('walletTracker.details.noHoldings', 'No Holdings Found')}
                </h3>
                <p style={{ fontSize: '14px', color: 'var(--muted)' }}>
                  {activeProvider === 'None'
                    ? t('walletTracker.details.noProvider', 'No data provider configured. Add DeBank or Moralis API keys.')
                    : t('walletTracker.details.noHoldingsDesc', 'This wallet doesn\'t hold any tokens')}
                </p>
              </div>
            )}

            {/* All tokens are small - show message */}
            {sortedTokens.length === 0 && hiddenSmallTokensCount > 0 && (
              <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <Icons.Coins size={40} style={{ opacity: 0.3, marginBottom: '12px', color: TRACKER_COLORS.primary }} />
                <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '6px' }}>
                  {t('walletTracker.details.onlySmallTokens', 'Only Small Holdings')}
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--muted)' }}>
                  {t('walletTracker.details.allTokensSmall', 'This wallet has {{count}} token(s) but all are worth less than $1', { count: hiddenSmallTokensCount })}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* DeFi Positions Section */}
      {activeTab === 'defi' && (
      <div style={s.card}>
        <div style={s.sectionHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Icons.TrendingUp size={20} style={{ color: TRACKER_COLORS.purple }} />
            <h2 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>
              {t('walletTracker.details.defiPositions', 'DeFi Positions')}
            </h2>
            <span style={{
              background: `${TRACKER_COLORS.purple}20`,
              color: TRACKER_COLORS.purple,
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '12px',
              fontWeight: 600,
            }}>
              {defiPositions.length}
            </span>
          </div>
          {defiPositions.length > 0 && (
            <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
              {formatUSD(defiPositions.reduce((sum: number, p: DeFiPosition) => sum + p.netValueUSD, 0))} total
            </span>
          )}
        </div>

        {/* Loading state */}
        {isLoading && (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <div style={{
              width: '32px',
              height: '32px',
              border: `3px solid ${TRACKER_COLORS.purple}30`,
              borderTop: `3px solid ${TRACKER_COLORS.purple}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ fontSize: '14px', color: 'var(--muted)', margin: 0 }}>
              {t('walletTracker.details.loadingDeFi', 'Loading DeFi positions...')}
            </p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && defiPositions.length === 0 && (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <Icons.TrendingUp size={40} style={{ opacity: 0.3, marginBottom: '12px', color: TRACKER_COLORS.purple }} />
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '6px', color: 'var(--text)' }}>
              {isDebankCreditsExhausted()
                ? t('walletTracker.details.defiCreditsExhausted', 'DeBank Credits Exhausted')
                : t('walletTracker.details.noDefiPositions', 'No DeFi Positions')}
            </h3>
            <p style={{ fontSize: '13px', color: 'var(--muted)', margin: 0, maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
              {isDebankCreditsExhausted()
                ? t('walletTracker.details.defiCreditsExhaustedDesc', 'Your DeBank API credits are exhausted. Data is being served from cache when available. Recharge your credits to get fresh DeFi data.')
                : activeProvider === 'Moralis' || activeProvider === 'Alchemy'
                  ? t('walletTracker.details.defiLimited', 'DeFi data requires DeBank Pro API. Your DeBank API credits may be exhausted.')
                  : t('walletTracker.details.noDefiDesc', 'This wallet has no active DeFi positions (lending, staking, farming, etc.)')}
            </p>

            {/* Cache indicator */}
            {walletData?.lastUpdated?.includes('(cached)') && (
              <div style={{
                marginTop: '12px',
                padding: '8px 14px',
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.2)',
                borderRadius: '8px',
                fontSize: '12px',
                color: TRACKER_COLORS.warning,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}>
                <Icons.Clock size={14} />
                {t('walletTracker.details.usingCachedData', 'Using cached data')}
              </div>
            )}

            {(isDebankCreditsExhausted() || activeProvider === 'Moralis' || activeProvider === 'Alchemy') && (
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '16px', flexWrap: 'wrap' }}>
                <a
                  href="https://cloud.debank.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 20px',
                    background: `linear-gradient(135deg, ${TRACKER_COLORS.purple}, ${TRACKER_COLORS.purple}80)`,
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  <Icons.ExternalLink size={14} />
                  {t('walletTracker.details.rechargeDeBank', 'Recharge DeBank Credits')}
                </a>
                <button
                  onClick={() => {
                    clearWalletCache(address);
                    window.location.reload();
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 20px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  <Icons.RefreshCw size={14} />
                  {t('walletTracker.details.clearCacheRetry', 'Clear Cache & Retry')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Positions list */}
        {!isLoading && defiPositions.length > 0 && (
          <div>
            {defiPositions.map((position: DeFiPosition, idx: number) => (
              <div
                key={`${position.protocolId}-${position.type}-${idx}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px 24px',
                  borderBottom: idx < defiPositions.length - 1 ? '1px solid rgba(255, 255, 255, 0.04)' : 'none',
                }}
              >
                {/* Protocol Logo */}
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: 'rgba(139, 92, 246, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}>
                  {position.protocolLogo ? (
                    <img
                      src={position.protocolLogo}
                      alt={position.protocolName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <Icons.TrendingUp size={20} style={{ color: TRACKER_COLORS.purple, opacity: 0.6 }} />
                  )}
                </div>

                {/* Protocol Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', marginBottom: '2px' }}>
                    {position.protocolName}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{
                      padding: '2px 6px',
                      borderRadius: '4px',
                      background: 'rgba(139, 92, 246, 0.15)',
                      color: TRACKER_COLORS.purple,
                      fontSize: '10px',
                      textTransform: 'capitalize',
                    }}>
                      {position.type}
                    </span>
                    <span style={{
                      padding: '2px 6px',
                      borderRadius: '4px',
                      background: 'rgba(100, 100, 100, 0.2)',
                      color: 'var(--muted)',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                    }}>
                      {position.chain}
                    </span>
                  </div>
                </div>

                {/* Supplied Tokens */}
                {position.suppliedTokens && position.suppliedTokens.length > 0 && (
                  <div style={{ textAlign: 'right', minWidth: '120px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>Supplied</div>
                    {position.suppliedTokens.slice(0, 2).map((t, i) => (
                      <div key={i} style={{ fontSize: '12px', color: 'var(--text)' }}>
                        {formatTokenBalance(t.balance)} {t.symbol}
                      </div>
                    ))}
                    {position.suppliedTokens.length > 2 && (
                      <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                        +{position.suppliedTokens.length - 2} more
                      </div>
                    )}
                  </div>
                )}

                {/* Health Factor */}
                {position.healthFactor && (
                  <div style={{ textAlign: 'center', minWidth: '70px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>Health</div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: position.healthFactor > 2 ? TRACKER_COLORS.success :
                             position.healthFactor > 1.5 ? TRACKER_COLORS.warning : TRACKER_COLORS.error,
                    }}>
                      {position.healthFactor.toFixed(2)}
                    </div>
                  </div>
                )}

                {/* Net Value */}
                <div style={{ textAlign: 'right', minWidth: '100px' }}>
                  <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>
                    {formatUSD(position.netValueUSD)}
                  </div>
                  {position.apy && (
                    <div style={{ fontSize: '12px', color: TRACKER_COLORS.success }}>
                      {position.apy.toFixed(2)}% APY
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Alerts Section */}
      {walletAlerts.length > 0 && (
        <div style={{ ...s.card, marginTop: '24px' }}>
          <div style={s.sectionHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Icons.Bell size={20} style={{ color: TRACKER_COLORS.warning }} />
              <h2 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>
                {t('walletTracker.details.activeAlerts', 'Active Alerts')}
              </h2>
              <span style={{
                background: `${TRACKER_COLORS.warning}20`,
                color: TRACKER_COLORS.warning,
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: 600,
              }}>
                {walletAlerts.length}
              </span>
            </div>
          </div>

          <div>
            {walletAlerts.map((alert: Alert, idx: number) => {
              const conditionConfig = ALERT_CONDITIONS[alert.condition];
              return (
                <div
                  key={alert.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px 24px',
                    borderBottom: idx < walletAlerts.length - 1 ? '1px solid rgba(255, 255, 255, 0.04)' : 'none',
                  }}
                >
                  {/* Condition Icon */}
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: `${conditionConfig.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                  }}>
                    {conditionConfig.icon}
                  </div>

                  {/* Alert Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', marginBottom: '2px' }}>
                      {alert.tokenSymbol || 'All Tokens'} - {conditionConfig.label}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                      {conditionConfig.description}
                    </div>
                  </div>

                  {/* Channels */}
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {alert.channels.map((ch: AlertChannel) => (
                      <span key={ch} style={{
                        padding: '4px 8px',
                        borderRadius: '6px',
                        background: 'rgba(255, 255, 255, 0.08)',
                        fontSize: '10px',
                        color: 'var(--muted)',
                        textTransform: 'uppercase',
                      }}>
                        {ch === 'in_app' ? 'App' : ch}
                      </span>
                    ))}
                  </div>

                  {/* Toggle & Delete */}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => toggleAlert(alert.id)}
                      style={{
                        padding: '8px',
                        background: alert.enabled ? `${TRACKER_COLORS.success}20` : 'rgba(255, 255, 255, 0.05)',
                        border: 'none',
                        borderRadius: '8px',
                        color: alert.enabled ? TRACKER_COLORS.success : 'var(--muted)',
                        cursor: 'pointer',
                      }}
                      title={alert.enabled ? 'Disable alert' : 'Enable alert'}
                    >
                      {alert.enabled ? <Icons.Bell size={16} /> : <Icons.BellOff size={16} />}
                    </button>
                    <button
                      onClick={() => removeAlert(alert.id)}
                      style={{
                        padding: '8px',
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: 'none',
                        borderRadius: '8px',
                        color: TRACKER_COLORS.error,
                        cursor: 'pointer',
                      }}
                      title="Delete alert"
                    >
                      <Icons.Trash size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Alert Creation Modal */}
      {showAlertModal && selectedTokenForAlert && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(8px)',
          }}
          onClick={() => setShowAlertModal(false)}
        >
          <div
            style={{
              maxWidth: '480px',
              width: '95%',
              background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(10, 15, 30, 0.99))',
              borderRadius: '20px',
              border: '1px solid rgba(6, 182, 212, 0.2)',
              boxShadow: '0 24px 64px rgba(0, 0, 0, 0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 24px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
              background: 'rgba(245, 158, 11, 0.05)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: `linear-gradient(135deg, ${TRACKER_COLORS.warning}, ${TRACKER_COLORS.warning}80)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Icons.Bell size={20} color="white" />
                </div>
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: 600, margin: 0, color: 'var(--text)' }}>
                    {t('walletTracker.alerts.createAlert', 'Create Alert')}
                  </h3>
                  <p style={{ fontSize: '13px', color: 'var(--muted)', margin: 0 }}>
                    {selectedTokenForAlert.symbol} ({selectedTokenForAlert.name})
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowAlertModal(false)}
                style={{
                  padding: '8px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'var(--muted)',
                  cursor: 'pointer',
                }}
              >
                <Icons.X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              {/* Condition Selection */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 600,
                  marginBottom: '12px',
                  color: 'var(--text)',
                }}>
                  {t('walletTracker.alerts.alertWhen', 'Alert me when...')}
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                  {(['token_buy', 'token_sell', 'token_transfer', 'swap'] as AlertCondition[]).map(condition => {
                    const config = ALERT_CONDITIONS[condition];
                    const isSelected = alertCondition === condition;
                    return (
                      <button
                        key={condition}
                        type="button"
                        onClick={() => setAlertCondition(condition)}
                        style={{
                          padding: '14px',
                          background: isSelected ? `${config.color}20` : 'rgba(255, 255, 255, 0.05)',
                          border: isSelected ? `2px solid ${config.color}` : '2px solid rgba(255, 255, 255, 0.08)',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          transition: 'all 0.2s',
                        }}
                      >
                        <div style={{ fontSize: '20px', marginBottom: '6px' }}>{config.icon}</div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: isSelected ? config.color : 'var(--text)' }}>
                          {config.label}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>
                          {config.description}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Channel Selection */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 600,
                  marginBottom: '12px',
                  color: 'var(--text)',
                }}>
                  {t('walletTracker.alerts.notifyVia', 'Notify me via')}
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {([
                    { id: 'in_app' as AlertChannel, label: 'In-App', icon: '🔔' },
                    { id: 'email' as AlertChannel, label: 'Email', icon: '📧' },
                    { id: 'webhook' as AlertChannel, label: 'Webhook', icon: '🔗' },
                  ]).map(channel => {
                    const isSelected = alertChannels.includes(channel.id);
                    return (
                      <button
                        key={channel.id}
                        type="button"
                        onClick={() => toggleChannel(channel.id)}
                        style={{
                          flex: 1,
                          padding: '12px',
                          background: isSelected ? `${TRACKER_COLORS.primary}20` : 'rgba(255, 255, 255, 0.05)',
                          border: isSelected ? `2px solid ${TRACKER_COLORS.primary}` : '2px solid rgba(255, 255, 255, 0.08)',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.2s',
                        }}
                      >
                        <span style={{ fontSize: '18px' }}>{channel.icon}</span>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: isSelected ? TRACKER_COLORS.primary : 'var(--muted)',
                        }}>
                          {channel.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Info Box */}
              <div style={{
                padding: '14px 16px',
                background: 'rgba(6, 182, 212, 0.08)',
                borderRadius: '12px',
                border: '1px solid rgba(6, 182, 212, 0.15)',
                fontSize: '12px',
                color: 'var(--muted)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
              }}>
                <Icons.Info size={16} style={{ color: TRACKER_COLORS.primary, flexShrink: 0, marginTop: '2px' }} />
                <span>
                  {t('walletTracker.alerts.infoText', 'You\'ll be notified when this wallet performs the selected action with')} <strong style={{ color: TRACKER_COLORS.primary }}>{selectedTokenForAlert.symbol}</strong>.
                </span>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              padding: '20px 24px',
              borderTop: '1px solid rgba(255, 255, 255, 0.06)',
              background: 'rgba(0, 0, 0, 0.2)',
            }}>
              <button
                onClick={() => setShowAlertModal(false)}
                style={{
                  padding: '12px 20px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '10px',
                  color: 'var(--text)',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleCreateAlert}
                disabled={alertChannels.length === 0}
                style={{
                  padding: '12px 24px',
                  background: alertChannels.length === 0
                    ? 'rgba(100, 100, 100, 0.3)'
                    : `linear-gradient(135deg, ${TRACKER_COLORS.warning}, ${TRACKER_COLORS.warning}80)`,
                  border: 'none',
                  borderRadius: '10px',
                  color: alertChannels.length === 0 ? 'rgba(150, 150, 150, 0.6)' : 'white',
                  cursor: alertChannels.length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: alertChannels.length === 0 ? 'none' : `0 4px 16px ${TRACKER_COLORS.warning}40`,
                }}
              >
                <Icons.Bell size={16} />
                {t('walletTracker.alerts.createAlertBtn', 'Create Alert')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spin Animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
